import { useEffect, useRef } from 'react';

export const MAIN_CONTENT_ID = 'main-content';

const HEADING_WAIT_MS = 3000;

const shouldKeepFocus = (element: Element | null) =>
    !!element?.matches?.('input, textarea, select, [contenteditable="true"]') || !!element?.closest?.('#modal-root');

export const useRouteFocus = (pathname: string) => {
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return undefined;
        }

        const activeAtNavigation = document.activeElement;
        const main = document.getElementById(MAIN_CONTENT_ID);

        if (!main || shouldKeepFocus(activeAtNavigation)) {
            return undefined;
        }

        const focusTarget = (target: HTMLElement) => {
            const { activeElement } = document;
            if (activeElement !== document.body && activeElement !== activeAtNavigation) {
                return;
            }

            if (!target.hasAttribute('tabindex')) {
                target.setAttribute('tabindex', '-1');
            }
            target.focus({ preventScroll: true });
        };

        const heading = main.querySelector('h1');
        if (heading) {
            focusTarget(heading);
            return undefined;
        }

        let timeoutId: number;
        const observer = new MutationObserver(() => {
            const loadedHeading = main.querySelector('h1');
            if (loadedHeading) {
                observer.disconnect();
                window.clearTimeout(timeoutId);
                focusTarget(loadedHeading);
            }
        });

        observer.observe(main, { childList: true, subtree: true });
        timeoutId = window.setTimeout(() => {
            observer.disconnect();
            focusTarget(main);
        }, HEADING_WAIT_MS);

        return () => {
            observer.disconnect();
            window.clearTimeout(timeoutId);
        };
    }, [pathname]);
};
